import { GridOptions, ICellRenderer, ICellRendererParams } from 'ag-grid-community';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { GroupDTO, LoginApiModel } from 'src/app/reusables/assign-tool/angular-assign-tool/angular-assign-tool.models';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { DashboardService } from '../../dashboard.service';
import { AccessChanges, AccessGroup, AccessLogin, AccessProfile, DashboardAccessPermissions, SelectedEmployee, SelectedTeam, SelectionType } from './dashboard-access.model';
import { DashboardAccessService } from './dashboard-access.service';
import { PermissionCellRendererComponent } from './permission-cell-renderer/permission-cell-renderer.component';
import { AgGridAngular } from 'ag-grid-angular';
import { DashboardAccessOptions } from 'src/shared/models/dashboard.model';

@Component({
    selector: 'cv-dashboard-access',
    templateUrl: './dashboard-access.component.html',
    styleUrls: ['./dashboard-access.component.scss'],
})
export class DashboardAccessComponent implements OnInit {
    @ViewChild('teamsTab') teamsTab;
    @ViewChild('employeesTab') employeesTab;
    @ViewChild('dashboardAccessTeamGrid') agGridTeam: AgGridAngular;
    @ViewChild('dashboardAccessEmployeeGrid') agGridEmployee: AgGridAngular;

    private modalData: DashboardAccessOptions;

    // search filters
    public filterValue = '';
    public teamFilterValue = '';
    public employeeFilterValue = '';

    // controls the switches and radio buttons
    public shareDashboard = false;
    public selection: SelectionType = SelectionType.None;
    public allEmployees: SelectionType = SelectionType.AllEmployees;
    public custom: SelectionType = SelectionType.Custom;

    // lists needed to pass data for updating dashboard permissions
    public selectedTeamsList: Map<number, SelectedTeam> = new Map<number, SelectedTeam>();
    public selectedEmployeesList: Map<number, SelectedEmployee> = new Map<number, SelectedEmployee>();
    private accessChanges: AccessChanges = null;
    public numberOfChanges = 0;
    // list of all profiles in the company, needed for promise in updating permissions
    private companyProfiles;

    // teams list variables (list of all groups, list of selected teams)
    public teams: GroupDTO[] = [];
    public selectedTeams: GroupDTO[] = [];
    public checkedTeams: GroupDTO[] = [];
    public isTeamsLoaded = false;

    // employees list variables (list of all employees, list of selected employees)
    public employees: LoginApiModel[] = [];
    public selectedEmployees: LoginApiModel[] = [];
    public checkedEmployees: LoginApiModel[] = [];
    public isEmpsLoaded = false;

    // grid options
    public teamGridOptions: GridOptions = null;
    public employeeGridOptions: GridOptions = null;
    private setupTeamGridTimer: number;
    private setupEmpGridTimer: number;

    // needed for cv-loader
    public showLoader = false;
    public initialized = false;

    // no results
    public noResults: string;
    public hasCurrentShares = false;

    // for localization
    public employeesLocal: string;
    public teamsLocal: string;

    public isSaving = false;

    public isPublic = false;

    noTeams = "";
    noEmployees = "";

    constructor(
        public activeModal: NgbActiveModal,
        private agGridHelperService: AgGridHelperService,
        public cvLocalizationService: CVLocalizationService,
        private service: DashboardAccessService,
        private dashboardService: DashboardService,
        public  config: CvConfigurationService,
    ) {
        this.modalData = this.service._getModalData();
    }

    async ngOnInit(): Promise<void> {
        await this.cvLocalizationService.GetSection('Dashboard_Access');
        this.noResults = await this.cvLocalizationService.GetLocalText('Dashboard_Access', 'No_Results', 'No results found');
        this.employeesLocal = await this.cvLocalizationService.GetLocalText('Dashboard_Access', 'Employees', 'Employees');
        this.teamsLocal = await this.cvLocalizationService.GetLocalText('Dashboard_Access', 'Teams', 'Teams');
        this.noTeams = await this.cvLocalizationService.GetLocalText('Dashboard_Access', 'No_Teams', 'No teams selected yet');
        this.noEmployees = await this.cvLocalizationService.GetLocalText('Dashboard_Access', 'No_Employees', 'No employees selected yet');
        this.accessChanges = new AccessChanges();
        this.setupGrid();
        await this.loadData();
        this.companyProfiles = await this.service.loadRoles();
        if (this.modalData.dashboard.IsPublic) {
            this.shareDashboard = true;
            this.selection = this.allEmployees;
        }
        this.isPublic = this.modalData.dashboard.IsPublic;
        this.showLoader = false;
        this.initialized = true;
    }

    async loadData() {
        this.showLoader = true;
        await this.updateSelectedTeamList();
        await this.updateSelectedEmployeeList();
        this.showLoader = false;
    }

    // creating grid options for teams and employees
    async setupGrid() {
        // team grid options
        this.teamGridOptions = this.agGridHelperService.createGridOptions({
            onGridReady: params => {
                if (this.selectedTeamsList.size > 0) {
                    this.queueUpdateTeamGrid();
                    this.agGridTeam.api.sizeColumnsToFit();
                }
                this.applyTeamFilter();
            },
            getRowId: ({data: rowData}) => rowData.data.GroupID.toString(),
            headerHeight: 0,
            context: {
                delete: this.deleteTeam.bind(this),
            },
            onRowDataUpdated: () => this.agGridEmployee?.api?.sizeColumnsToFit(),
            overlayNoRowsTemplate: null,
            suppressNoRowsOverlay: true,
        });

        this.teamGridOptions.columnDefs.push({
            field: 'name',
            cellRenderer: TeamCellRenderer
        });
        this.teamGridOptions.columnDefs.push({
            cellRenderer: DeleteButtonRenderer,
            width: 80
        });

        // employee grid options
        this.employeeGridOptions = this.agGridHelperService.createGridOptions({
            onGridReady: params => {
                if (this.selectedEmployeesList.size > 0) {
                    this.queueUpdateEmployeeGrid();
                    this.agGridEmployee.api.sizeColumnsToFit();
                }
                this.applyEmployeeFilter();
            },
            getRowId: ({data: rowData}) => rowData.data.LoginID.toString(),
            headerHeight: 0,
            context: {
                delete: this.deleteEmployee.bind(this),
                update: this.updatePermission.bind(this),
            },
            onRowDataUpdated: () => this.agGridEmployee?.api?.sizeColumnsToFit(),
            overlayNoRowsTemplate: null,
            suppressNoRowsOverlay: true,
        });

        this.employeeGridOptions.columnDefs.push({
            field: 'name',
            cellRenderer: EmployeeCellRenderer
        });
        this.employeeGridOptions.columnDefs.push({
            field: 'permission',
            cellRenderer: PermissionCellRendererComponent,
            cellClass: 'text-overflow'
        });
        this.employeeGridOptions.columnDefs.push({
            cellRenderer: DeleteButtonRenderer,
            width: 80
        });
    }

    // update the row data on ag grid for teams
    queueUpdateTeamGrid() {
        if (!this.agGridTeam?.api)
            return;

        if (this.setupTeamGridTimer) {
            clearTimeout(this.setupTeamGridTimer);
            this.setupTeamGridTimer = null;
        }

        const teamList = [];
        this.selectedTeamsList.forEach(team => {
            teamList.push(team);
        });

        teamList.sort((a, b) => {
            const name1: string = a.name.toUpperCase().trim();
            const name2: string = b.name.toUpperCase().trim();
            return (name1 > name2) ? 1 : -1;
        });

        this.agGridTeam.api.setGridOption('rowData', teamList);
        this.agGridTeam.api.sizeColumnsToFit();
    }

    // update the row data on ag grid for employees
    queueUpdateEmployeeGrid() {
        if (!this.agGridEmployee?.api)
            return;

        if (this.setupEmpGridTimer) {
            clearTimeout(this.setupEmpGridTimer);
            this.setupEmpGridTimer = null;
        }

        const empList = [];
        this.selectedEmployeesList.forEach(emp => {
            empList.push(emp);
        });

        empList.sort((a, b) => {
            const name1 = a.name.toUpperCase().trim();
            const name2 = b.name.toUpperCase().trim();
            if (name1 === name2) return 0;
            return (name1 > name2) ? 1 : -1;
        });

        this.agGridEmployee.api.setGridOption('rowData', empList);
        this.agGridEmployee.api.sizeColumnsToFit();
    }

    // updating selected teams list
    async updateSelectedTeamList() {
        // initial load of teams
        if (!this.isTeamsLoaded) {
            const loadedTeams = await this.service.loadTeams();
            loadedTeams.forEach(team => {
                this.teams.push(team.groupDTO);
            });

            if (this.modalData.dashboard.RecipientGroups) {
                const teamsTemp = new Map<string, any>(Object.entries(this.modalData.dashboard.RecipientGroups));

                // unlike RecipientLogins, RecipientGroups is initialized so need to check if it is empty
                if (teamsTemp.size > 0) {
                    teamsTemp.forEach(team => {
                        const teamPermission = team.Profiles[0].readTheirs
                            ? DashboardAccessPermissions.ReadTheirs : DashboardAccessPermissions.None;
                        this.selectedTeamsList.set(team.Group.GroupID, {
                            name: team.Group.GroupName,
                            data: team.Group,
                            permission: teamPermission
                        });
                        this.selectedTeams.push(team.Group);
                        this.checkedTeams.push(team.Group);
                    });

                    // controls the switch and radio buttons
                    this.shareDashboard = true;
                    this.selection = this.custom;
                    this.hasCurrentShares = true;
                }
            } else {
                this.teamsTab.isActive = false;
                this.employeesTab.isActive = true;
            }
            this.isTeamsLoaded = true;
        }

        this.queueUpdateTeamGrid();
    }

    // check team lists to see if we need to add or delete objects
    async checkSelectedTeamsList() {
        const compareList = this.checkedTeams.map(team => team.GroupID);
        if (this.selectedTeams.length === this.checkedTeams.length && this.selectedTeams.every((element, index) => {
            if (element.GroupID === compareList[index]) return true;
            return false;
        })) return;

        this.selectedTeams = Array.from(this.checkedTeams);

        if (this.selectedTeams.length === this.selectedTeamsList.size) {
            this.selectedTeamsList.forEach(team => {
                const checkedTeam = this.selectedTeams.find(t => t.GroupID === team.data.GroupID);
                if (!checkedTeam) {
                    this.selectedTeamsList.delete(team.data.GroupID);
                    this.deleteTeam(team, true);
                }
            });
        }

        if (this.selectedTeamsList.size < this.selectedTeams.length) {
            this.selectedTeams.forEach(team => {
                const selectedTeam = {
                    name: team.GroupName,
                    data: team,
                    permission: DashboardAccessPermissions.ReadTheirs, // default permission to 'View Only'
                };
                if (!this.selectedTeamsList.has(selectedTeam.data.GroupID)) {
                    this.selectedTeamsList.set(selectedTeam.data.GroupID, selectedTeam);
                }
                const accessGroup: AccessGroup = {
                    group: team,
                    profiles: new Map(),
                    permission: DashboardAccessPermissions.ReadTheirs
                };
                this.companyProfiles?.forEach(p => {
                    const profileToUpdate: AccessProfile = {
                        profile: p,
                        permission: DashboardAccessPermissions.ReadTheirs
                    };
                    accessGroup.profiles.set(p.ProfileID, profileToUpdate);
                });
                this.accessChanges.groups.set(team.GroupID, accessGroup);
            });
            this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;
            this.queueUpdateTeamGrid();
            return;
        }

        if (this.selectedTeams.length < this.selectedTeamsList.size) {
            this.selectedTeamsList.forEach(team => {
                const tempTeam: GroupDTO = this.selectedTeams.find(t => t.GroupID === team.data.GroupID);

                if (!tempTeam) {
                    this.selectedTeamsList.delete(team.data.GroupID);

                    this.deleteTeam(team, true);
                }
            });

            this.queueUpdateTeamGrid();
            return;
        }
    }

    // updated selected employee list
    async updateSelectedEmployeeList() {
        // initial load of employees
        if (!this.isEmpsLoaded) {
            this.employees = await this.service.loadAgents();

            const ownerIndex = this.employees.findIndex(x => x.LoginID === this.modalData.dashboard.CreatorID);
            if (ownerIndex >= 0) this.employees.splice(ownerIndex, 1);

            this.employees.sort((a, b) => {
                const name1 = a.FullName.toUpperCase().trim();
                const name2 = b.FullName.toUpperCase().trim();
                if (name1 === name2) return 0;
                return (name1 > name2) ? 1 : -1;
            });

            if (this.modalData.dashboard.RecipientLogins) {
                this.modalData.dashboard.RecipientLogins.forEach(agent => {
                    const agentPermission = agent.writeTheirs
                        ? DashboardAccessPermissions.WriteTheirs : DashboardAccessPermissions.ReadTheirs;

                    this.selectedEmployeesList.set(agent.LoginID, {
                        name: agent.FullName,
                        data: agent,
                        permission: agentPermission,
                    });
                    this.selectedEmployees.push(agent);
                    this.checkedEmployees.push(agent);
                });

                // controls the switch and radio buttons
                this.shareDashboard = true;
                this.selection = this.custom;
                this.hasCurrentShares = true;
            }
            this.isEmpsLoaded = true;
        }

        this.queueUpdateEmployeeGrid();
    }

    // check employee lists to see if we need to add or delete object(s)
    async checkSelectedEmpsList() {
        const compareList = this.checkedEmployees.map(emp => emp.LoginID);
        if (this.selectedEmployees.length === this.checkedEmployees.length && this.selectedEmployees.every((element, index) => {
            if (element.LoginID === compareList[index]) return true;
            return false;
        })) return;

        this.selectedEmployees = Array.from(this.checkedEmployees);

        if (this.selectedEmployees.length === this.selectedEmployeesList.size) {
            this.selectedEmployeesList.forEach(emp => {
                const checkedEmp = this.selectedEmployees.find(e => e.LoginID === emp.data.LoginID);
                if (!checkedEmp) {
                    this.selectedEmployeesList.delete(emp.data.LoginID);
                    this.deleteEmployee(emp, true);
                }
            });
        }

        if (this.selectedEmployeesList.size < this.selectedEmployees.length) {
            this.selectedEmployees.forEach(emp => {
                const selectedEmployee = {
                    name: emp.FullName,
                    data: emp,
                    permission: DashboardAccessPermissions.ReadTheirs, // default permission to 'View Only'
                };
                if (!this.selectedEmployeesList.has(selectedEmployee.data.LoginID)) {
                    this.selectedEmployeesList.set(selectedEmployee.data.LoginID, selectedEmployee);
                    const accessLogin: AccessLogin = {
                        login: emp,
                        permission: DashboardAccessPermissions.ReadTheirs
                    };
                    this.accessChanges.logins.set(emp.LoginID, accessLogin);
                }
            });
            this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;

            this.queueUpdateEmployeeGrid();
            return;
        }

        if (this.selectedEmployees.length < this.selectedEmployeesList.size) {
            this.selectedEmployeesList.forEach(emp => {
                const tempEmp: LoginApiModel = this.selectedEmployees.find(e => e.LoginID === emp.data.LoginID);
                if (!tempEmp) {
                    this.selectedEmployeesList.delete(emp.data.LoginID);
                    this.deleteEmployee(emp, true);
                }
            });
            this.queueUpdateEmployeeGrid();
            return;
        }
    }

    // search methods
    applyTeamFilter() {
        if (this.agGridTeam?.api) {
            this.teamFilterValue = this.teamFilterValue.trim();
            this.agGridTeam.api.setGridOption('quickFilterText', this.teamFilterValue);
        }
        if (this.selectedTeamsList?.size > 0) {
            this.agGridTeam.api.hideOverlay();
            this.agGridTeam.api.setGridOption('suppressNoRowsOverlay', true);
            if (this.agGridTeam.api.getDisplayedRowCount() === 0 && this.teamFilterValue) {
                const noResultsIllustration =
                    `<div>` +
                    `<svg class="mb-2" style="width: 42px; height: 32px;">` +
                    `<use href="../App_Themes/CXone/icons/no-search-results.svg#no-search-results"></use>` +
                    `</svg>` +
                    `<p>${this.noResults}</p>` +
                    `</div>`;
                this.agGridTeam.api.setGridOption('overlayNoRowsTemplate', noResultsIllustration);
                this.agGridTeam.api.setGridOption('suppressNoRowsOverlay', false);
                this.agGridTeam.api.showNoRowsOverlay();
            }
        } else {
            const noTeamsIllustration =
                `<div>` +
                `<svg class="mb-2" style="width: 42px; height: 32px;">` +
                `<use href="../App_Themes/CXone/icons/empty.svg#empty"></use>` +
                `</svg>` +
                `<p>${this.noTeams}</p>` +
                `</div>`;
            this.agGridTeam.api.setGridOption('overlayNoRowsTemplate', noTeamsIllustration);
            this.agGridTeam.api.setGridOption('suppressNoRowsOverlay', false);
            this.agGridTeam.api.showNoRowsOverlay();
        }
    }

    clearTeamFilter() {
        this.teamFilterValue = "";
        this.applyTeamFilter();
    }

    applyEmployeeFilter() {
        if (this.agGridEmployee?.api) {
            this.employeeFilterValue = this.employeeFilterValue.trim();
            this.agGridEmployee.api.setGridOption('quickFilterText', this.employeeFilterValue);
        }
        if (this.selectedEmployeesList?.size > 0) {
            this.agGridEmployee.api.hideOverlay();
            this.agGridEmployee.api.setGridOption('suppressNoRowsOverlay', true);
            if (this.agGridEmployee.api.getDisplayedRowCount() === 0 && this.employeeFilterValue) {
                const noResultsIllustration =
                    `<div>` +
                    `<svg class="mb-2" style="width: 42px; height: 32px;">` +
                    `<use href="../App_Themes/CXone/icons/no-search-results.svg#no-search-results"></use>` +
                    `</svg>` +
                    `<p>${this.noResults}</p>` +
                    `</div>`;
                this.agGridEmployee.api.setGridOption('overlayNoRowsTemplate', noResultsIllustration);
                this.agGridEmployee.api.setGridOption('suppressNoRowsOverlay', false);
                this.agGridEmployee.api.showNoRowsOverlay();
            }
        } else {
            const noEmployeesIllustration =
                `<div>` +
                `<svg class="mb-2" style="width: 42px; height: 32px;">` +
                `<use href="../App_Themes/CXone/icons/empty.svg#empty"></use>` +
                `</svg>` +
                `<p>${this.noEmployees}</p>` +
                `</div>`;
            this.agGridEmployee.api.setGridOption('overlayNoRowsTemplate', noEmployeesIllustration);
            this.agGridEmployee.api.setGridOption('suppressNoRowsOverlay', false);
            this.agGridEmployee.api.showNoRowsOverlay();
        }
    }

    clearEmployeeFilter() {
        this.employeeFilterValue = "";
        this.applyEmployeeFilter();
    }
    //#endregion Selected items filter

    checkSelection() {
        if (!this.shareDashboard) {
            this.selection = SelectionType.None;
        }
        else
            this.selection = this.custom;
    }

    // saves the permissions selected to the database
    async onSaveClicked() {
        if (this.isSaving) return;

        this.showLoader = true;
        this.isSaving = true;
        this.isPublic = this.selection === this.allEmployees;

        // if share switch is not active, delete all shared permisions and mark as not public
        if (!this.shareDashboard) {
            this.removeCurrentPermissions();
            this.removeTeamPermissions(true);
            this.removeEmpPermissions(true);
        }

        // if share switch is active, delete all shared permissions and mark as public
        if (this.selection === this.allEmployees) {
            this.removeCurrentPermissions();
            this.removeTeamPermissions(true);
            this.removeEmpPermissions(true);
        }

        const updatedDashboard = await this.dashboardService.publishChanges(this.modalData.dashboard.DashboardKey);
        await this.service.UpdateDashboardsAccesses([this.modalData.dashboard.DashboardKey], this.accessChanges, this.isPublic);

        await this.dashboardService.loadExistingDashboardsAndGroups();
        this.isSaving = false;
        await this.activeModal.close(updatedDashboard);
    }

    updatePermission(emp: SelectedEmployee) {
        const accessLogin: AccessLogin = {
            login: emp.data,
            permission: emp.permission
        };
        this.accessChanges.logins.set(emp.data.LoginID, accessLogin);
        this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;
    }

    // removes group/team and employee permissions for the selected dashboard
    removeCurrentPermissions() {
        if (this.modalData.dashboard.RecipientGroups) {
            const teamsTemp = new Map<string, any>(Object.entries(this.modalData.dashboard.RecipientGroups));
            teamsTemp.forEach(team => {
                const accessGroup: AccessGroup = {
                    group: team.Group,
                    profiles: new Map(),
                    permission: DashboardAccessPermissions.None
                };
                team.Profiles.forEach(p => {
                    const profileToAdd: AccessProfile = {
                        profile: p,
                        permission: DashboardAccessPermissions.None
                    };
                    accessGroup.profiles.set(p.ProfileID, profileToAdd);
                });
                this.accessChanges.groups.set(team.Group.GroupID, accessGroup);
            });
        }
        if (this.modalData.dashboard.RecipientLogins) {
            this.modalData.dashboard.RecipientLogins.forEach(agent => {
                const accessLogin: AccessLogin = {
                    login: agent,
                    permission: DashboardAccessPermissions.None
                };
                this.accessChanges.logins.set(agent.LoginID, accessLogin);
            });
        }
        this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;
    }

    async removeTeamPermissions(keepLoader: boolean = false) {
        this.showLoader = true;

        if (this.accessChanges.groups.size > 0) {
            this.accessChanges.groups = new Map<number, AccessGroup>();
            this.numberOfChanges = this.accessChanges.logins.size;
        }

        if (this.modalData.dashboard.RecipientGroups) {
            const teamsTemp = new Map<string, any>(Object.entries(this.modalData.dashboard.RecipientGroups));

            if (teamsTemp.size !== 0) {
                teamsTemp.forEach(team => {
                    const accessGroup = {
                        group: team.Group,
                        profiles: new Map(),
                        permission: DashboardAccessPermissions.None
                    };

                    this.companyProfiles.forEach(p => {
                        const profileToDelete = {
                            profile: p,
                            permission: DashboardAccessPermissions.None
                        };
                        accessGroup.profiles.set(p.ProfileID, profileToDelete);

                        this.accessChanges.groups.set(team.Group.GroupID, accessGroup);
                    });
                    this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;

                    this.selectedTeamsList.delete(team.GroupID);
                    const selectedIndex = this.selectedTeams.indexOf(team);
                    if (selectedIndex > -1) {
                        this.selectedTeams.splice(selectedIndex, 1);
                    }
                    const selectedCheckIndex = this.checkedTeams.indexOf(team);
                    if (selectedCheckIndex > -1) {
                        this.checkedTeams.splice(selectedCheckIndex, 1);
                    }
                    this.checkedTeams = this.checkedTeams.slice();
                });
            }
        }

        this.selectedTeams = [];
        this.checkedTeams = [];
        this.selectedTeamsList = new Map<number, SelectedTeam>();
        await this.queueUpdateTeamGrid();
        if (!keepLoader) this.showLoader = false;

        this.applyTeamFilter();
    }

    async removeEmpPermissions(keepLoader: boolean = false) {
        this.showLoader = true;

        if (this.accessChanges.logins.size > 0) {
            this.accessChanges.logins = new Map<number, AccessLogin>();
            this.numberOfChanges = this.accessChanges.groups.size;
        }

        if (this.modalData.dashboard.RecipientLogins !== null) {
            this.modalData.dashboard.RecipientLogins.forEach(agent => {
                const accessLogin: AccessLogin = {
                    login: agent,
                    permission: DashboardAccessPermissions.None
                };

                this.accessChanges.logins.set(agent.LoginID, accessLogin);
                this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;

                this.selectedEmployeesList.delete(agent.LoginID);
                const selectedIndex = this.selectedEmployees.indexOf(agent);
                if (selectedIndex > -1) {
                    this.selectedEmployees.splice(selectedIndex, 1);
                }
                const selectedCheckIndex = this.checkedEmployees.indexOf(agent);
                if (selectedCheckIndex > -1) {
                    this.checkedEmployees.splice(selectedCheckIndex, 1);
                }
                this.checkedEmployees = this.checkedEmployees.slice();
            });
        }

        this.selectedEmployees = [];
        this.checkedEmployees = [];
        this.selectedEmployeesList = new Map<number, SelectedEmployee>();
        await this.queueUpdateEmployeeGrid();
        if (!keepLoader) this.showLoader = false;

        this.applyEmployeeFilter();
    }

    onCancelClicked() {
        this.activeModal.dismiss(this.modalData.dashboard);
    }

    // deletes the selected team's permissions
    private async deleteTeam(selected: SelectedTeam, deleteFromDropdown: boolean = false) {
        this.showLoader = true;

        if (this.modalData.dashboard.RecipientGroups) {
            const teamsTemp = new Map<string, any>(Object.entries(this.modalData.dashboard.RecipientGroups));
            if (teamsTemp.has(selected.data.GroupID.toString())) {
                const accessGroup = {
                    group: selected.data,
                    profiles: new Map(),
                    permission: DashboardAccessPermissions.None
                };

                this.companyProfiles?.forEach(p => {
                    const profileToDelete = {
                        profile: p,
                        permission: DashboardAccessPermissions.None
                    };
                    accessGroup.profiles.set(p.ProfileID, profileToDelete);

                    this.accessChanges.groups.set(selected.data.GroupID, accessGroup);
                });
            }
        } else {
            this.accessChanges.groups.delete(selected.data.GroupID);
        }

        this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;

        this.selectedTeamsList.delete(selected.data.GroupID);
        const selectedIndex = this.selectedTeams.indexOf(selected.data);
        if (selectedIndex > -1) {
            this.selectedTeams.splice(selectedIndex, 1);
        }
        const selectedCheckIndex = this.checkedTeams.indexOf(selected.data);
        if (selectedCheckIndex > -1) {
            this.checkedTeams.splice(selectedCheckIndex, 1);
        }
        this.checkedTeams = this.checkedTeams.slice();
        if (!deleteFromDropdown) this.queueUpdateTeamGrid();
        this.showLoader = false;
    }

    // deletes the selected employee's permissions
    private async deleteEmployee(selected: SelectedEmployee, deleteFromDropdown: boolean = false) {
        this.showLoader = true;

        if (this.modalData.dashboard.RecipientLogins?.find(x => x.LoginID === selected.data.LoginID)) {
            const accessLogin: AccessLogin = {
                login: selected.data,
                permission: DashboardAccessPermissions.None
            };

            this.accessChanges.logins.set(selected.data.LoginID, accessLogin);
        } else {
            this.accessChanges.logins.delete(selected.data.LoginID);
        }
        this.numberOfChanges = this.accessChanges.groups.size + this.accessChanges.logins.size;

        this.selectedEmployeesList.delete(selected.data.LoginID);
        const selectedIndex = this.selectedEmployees.indexOf(selected.data);
        if (selectedIndex > -1) {
            this.selectedEmployees.splice(selectedIndex, 1);
        }
        const selectedCheckIndex = this.checkedEmployees.indexOf(selected.data);
        if (selectedCheckIndex > -1) {
            this.checkedEmployees.splice(selectedCheckIndex, 1);
        }
        this.checkedEmployees = this.checkedEmployees.slice();
        if (!deleteFromDropdown) this.queueUpdateEmployeeGrid();
        this.showLoader = false;
    }
}

// cell renderer used to add delete button to ag grid
class DeleteButtonRenderer implements ICellRenderer {
    element: HTMLDivElement;

    init(params: ICellRendererParams) {
        this.onUpdate(params);
    }

    getGui() { return this.element; }

    refresh(params: ICellRendererParams) {
        this.onUpdate(params);

        return true;
    }

    private onUpdate(params: ICellRendererParams) {
        this.element = document.createElement("div");
        $(this.element).addClass('d-flex align-cell-middle justify-content-end');
        this.element.innerHTML = `<button type="button" class="icon-btn fill-valyrianSteel">` +
            `<svg class="icon-16 fill-cxone-delete fill-hover-cerulean" style="margin-top: 8px">` +
            `<use href="${_BaseURI}/App_Themes/clearview-default/icons/utility/close.svg#close">` +
            `</use></svg></button>`;
        $(this.element).find("button")[0].addEventListener("click", () => params.context.delete(params.data));
    }
}

// cell renderer used to add group icon to ag grid
class TeamCellRenderer implements ICellRenderer {
    element: HTMLDivElement;

    init(params: ICellRendererParams) {
        this.onUpdate(params);
    }

    getGui() { return this.element; }

    refresh(params: ICellRendererParams) {
        this.onUpdate(params);

        return true;
    }

    private onUpdate(params: ICellRendererParams) {
        this.element = document.createElement("div");
        $(this.element).addClass('d-flex');
        this.element.style.height = '44px';
        const name: string = params.data.name;
        this.element.innerHTML = `<svg class="icon-24 fill-panther" style="margin-top: 6px;">` +
            `<use href="${_BaseURI}/App_Themes/CXOne/icons/teams.svg#teams">` +
            `</use></svg>` +
            `<div class="ml-2" style="padding-top: 6px;">${name}</div>`;
    }
}

// cell renderer used to add user icon to ag grid
class EmployeeCellRenderer implements ICellRenderer {
    element: HTMLDivElement;

    init(params: ICellRendererParams) {
        this.onUpdate(params);
    }

    getGui() { return this.element; }

    refresh(params: ICellRendererParams) {
        this.onUpdate(params);

        return true;
    }

    private onUpdate(params: ICellRendererParams) {
        this.element = document.createElement("div");
        $(this.element).addClass('d-flex');
        this.element.style.height = '50px';
        let initials: string = params.data.data.FirstName.toString().charAt(0) + params.data.data.LastName.toString().charAt(0);
        if (!Boolean(initials)) initials = params.data.data.FullName.toString().charAt(0);
        initials = initials.toUpperCase();

        this.element.innerHTML =
            `<div class="d-flex"><div class="d-flex"` +
            `style="border-radius: 100%;` +
            `background-color: #dadadb; font-weight: bold; height: 2.25rem; width: 2.25rem; justify-content: center;` +
            `margin-top: 1.25px; padding-top: 6px;">${initials}</div>` +
            `<div class="ml-2" style="margin-top: 8px;">${params.data.name}</div></div>`;
    }
}
